import { Divider } from '@katalon-studio/katalon-ui/v2';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import InputBase from '@mui/material/InputBase';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import { unwrapResult } from '@reduxjs/toolkit';
import { CardCvcElement, CardExpiryElement, CardNumberElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe, StripeCardNumberElementChangeEvent, StripeElementChangeEvent } from '@stripe/stripe-js';
import clsx from 'clsx';
import { useSnackbar } from 'notistack';
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { LoadingProgress } from '../../../../layout';
import { CardBrand, Exception, PaymentMethod } from '../../../../models';
import { useReadOnlyMode } from '../../../../routes';
import { fromAccountUsers, fromAuth, useAppDispatch } from '../../../../store';
import { fromAccounts, fromPaymentMethod } from '../../../../store/rootReducer';
import { usePlanCartSnapshot } from '../../../../hook/usePlanCartSnapshot';
import { sendTrackingData } from '../../../../utils/utils';

import { PaymentMethodActionMenu } from '../PaymentMethodActionMenu';
import { withCartSnapshot } from '../../../../utils/analytics';
import { useGetTrackingCookies } from '../../../../hook/useGetTrackingCookies';
import { CARD_BRAND_LIST } from '../Constants';
import {
  CARD_CVC_OPTIONS,
  CARD_EXP_OPTIONS,
  CardInputEndWrapper,
  CardInputWrapper,
  CardNameWrapper,
  NUMBER_OPTIONS,
} from '../cardElementOptions';
import { CurrentPaymentCard } from './CurrentPaymentCard';
import { track } from '../../../../analytics';

const useStyles = makeStyles(theme => ({
  container: {
    padding: 0,
    width: '20rem',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: theme.palette.background.paper,
  },
  cardName: {
    width: '100%',
    fontSize: theme.spacing(1.75),
    fontSmoothing: 'antialiased',
    '&:focus': {
      color: '#081D36',
    },
    '&::placeholder': {
      color: '#A1A9B3',
      letterSpacing: 'normal',
      fontWeight: 100,
    },
    '&:focus::placeholder': {
      color: '#CFD7DF',
    },
  },
  paymentLogo: {
    height: theme.spacing(3),
    marginRight: theme.spacing(1),
    borderRadius: theme.spacing(0.325),
  },
  disablePaymentLogo: {
    filter: 'grayscale(100%)',
    opacity: '0.2',
  },
  cardInfoLabel: {
    fontWeight: theme.typography.fontWeightBold,
  },
  cancelBtn: {
    marginRight: theme.spacing(1),
    fontWeight: 500,
    height: 40,
    float: 'right',
    color: '#000000',
    width: 72,
    borderRadius: 'border-radius/border-radius-xs',
    borderWidth: '2px',
  },
  saveBtn: {
    fontWeight: 500,
    height: 40,
    width: 72,
    float: 'right',
    color: '#fff',
    backgroundColor: '#5959EB',
    '&:hover': {
      backgroundColor: '#43509B',
    },
  },
  button: {
    fontWeight: 500,
    height: 40,
    width: 72,
    float: 'right',
    color: '#000000',
    borderRadius: 'border-radius/border-radius-xs',
    borderWidth: '2px',
  },

  checkoutTitle: {
    paddingBottom: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
    fontWeight: '700',
  },
}));

const fieldTrackingInfoMapper: { [key: string]: any } = {
  cardNumber: {
    name: 'card_number',
    isRequired: true,
  },
  cardExpiry: {
    name: 'expiration',
    isRequired: true,
  },
  cardCvc: {
    name: 'cvc',
    isRequired: true,
  },
  cardHolder: {
    name: 'card_holder',
    isRequired: true,
  },
};

interface CardInformationProps {
  accountId: number;
  enableSave: boolean;
  enableDelete: boolean;
  currentCard?: PaymentMethod;
}

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY!);

const BuiltInPaymentFormInner = forwardRef((props: CardInformationProps, ref) => {
  const classes = useStyles();
  const intl = useIntl();
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDispatch();
  const elements = useElements();
  const stripe = useStripe();
  const user = useSelector(fromAuth.selectUser);
  const {
    accountId,
    enableSave,
    enableDelete,
    currentCard,
  } = props;
  const cartSnapshot = usePlanCartSnapshot();
  const trackingCookies = useGetTrackingCookies();
  const account = useSelector(fromAccounts.selectAccountById(Number(accountId)));
  const currentAccountUser = useSelector(fromAccountUsers.selectOneByUserIdAndAccountId(
    Number(user?.id),
    Number(accountId),
  ));
  const { enqueueSnackbar } = useSnackbar();
  const [name, setName] = useState('');
  const isCardFormOpen = useSelector(fromPaymentMethod.isCardEditing);
  const [currentCardBrand, setCurrentCardBrand] = useState('unknown');
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  const [isCardNumberValid, setIsCardNumberValid] = useState(false);
  const [isCardCvcValid, setIsCardCvcValid] = useState(false);
  const [isCardExpiryDateValid, setIsExpiryDateValid] = useState(false);
  const isReadOnly = useReadOnlyMode();

  const handleChangeName = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  };

  const handleCardHolderBlurred = (e: React.FocusEvent<HTMLInputElement>) => {
    if (e.target.value) {
      sendFilledFieldTracking('cardHolder', true);
    }
  };

  const handleCancel = () => {
    dispatch(fromPaymentMethod.doEnableCardEditing(false));
    setErrorMessage(undefined);
    setCurrentCardBrand('unknown');
    setName('');
    elements?.getElement(CardNumberElement)?.clear();
    elements?.getElement(CardExpiryElement)?.clear();
    elements?.getElement(CardCvcElement)?.clear();
    dispatch(fromPaymentMethod.doUpdateCardInformationInvalid(currentCard === undefined));
  };

  useEffect(() => {
    dispatch(fromPaymentMethod.doEnableCardEditing(!currentCard));
  }, [currentCard]);

  useEffect(() => {
    const isInvalid = errorMessage !== undefined || name === ''
      || !isCardExpiryDateValid || !isCardCvcValid || !isCardNumberValid;
    dispatch(fromPaymentMethod.doUpdateCardInformationInvalid(isInvalid));
  }, [errorMessage, name, elements]);

  useImperativeHandle(ref, () => ({
    saveCardInformation: submitPaymentMethod,
  }));

  const submitPaymentMethod = () => {
    if (!stripe || !elements || loading || !accountId) {
      return;
    }

    const doSubmit = async () => {
      setLoading(true);
      try {
        const cardNumberElement = elements.getElement(CardNumberElement);
        if (cardNumberElement) {
          const token = await stripe.createToken(cardNumberElement, {
            name,
          });
          if (token.token && currentCard) {
            await dispatch(fromPaymentMethod.doUpdatePaymentMethod({
              id: currentCard?.id,
              stripeToken: token.token.id,
            })).then(unwrapResult)
              .then(() => {
                handleCancel();
                enqueueSnackbar(<FormattedMessage id="billinginfo.cardinfo.update.success" />, { variant: 'success' });
                track(
                  'Payment Information Updated',
                  {
                    id: user?.id,
                    user_id: user?.email,
                    firstName: user?.firstName,
                    lastName: user?.lastName,
                    email: user?.email,
                    system_role: user?.roles,
                    org_id: account?.id,
                    org_name: account?.name,
                    org_role: currentAccountUser?.role,
                  },
                );
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                  user_id: user?.id,
                  org_role: currentAccountUser?.role,
                  system_role: user?.roles,
                  org_id: account?.id,
                  org_name: account?.name,
                  event: 'gtm_payment_information_updated',
                });
                sendSaveButtonTracking();
              })
              .catch(showErrorAndSendTracking);
          } else if (token.token && !currentCard) {
            setLoading(true);
            await dispatch(fromPaymentMethod.doCreatePaymentMethod({
              accountId: +accountId,
              stripeToken: token.token.id,
            })).then(unwrapResult)
              .then(() => {
                handleCancel();
                enqueueSnackbar(<FormattedMessage id="billinginfo.cardinfo.save.success" />, { variant: 'success' });
                track(
                  'Payment Information Saved',
                  {
                    id: user?.id,
                    user_id: user?.email,
                    firstName: user?.firstName,
                    lastName: user?.lastName,
                    email: user?.email,
                    system_role: user?.roles,
                    org_id: account?.id,
                    org_name: account?.name,
                    org_role: currentAccountUser?.role,
                  },
                );
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                  user_id: user?.id,
                  org_role: currentAccountUser?.role,
                  system_role: user?.roles,
                  org_id: account?.id,
                  org_name: account?.name,
                  event: 'gtm_payment_information_saved',
                });
                sendSaveButtonTracking();
              })
              .catch(showErrorAndSendTracking);
          } else if (token.error) {
            setErrorMessage('Invalid card information');
            // Invalid information from Stripe
            sendSaveButtonTracking(token.error.code);
          }
        }
      } finally {
        setLoading(false);
      }
    };
    doSubmit().catch(() => {
    });
  };

  const showErrorAndSendTracking = (ex: Exception) => {
    sendSaveButtonTracking(ex.message);
  };

  const sendSaveButtonTracking = (error?: string) => {
    const isValid = !error;
    const additionInfo = withCartSnapshot({
      field: 'payment',
      object: 'button',
      action: 'save',
      is_required: true,
      is_valid: isValid,
      error,
      ...trackingCookies,
    }, cartSnapshot);

    sendTrackingData('checkout_page_all_action', accountId, undefined, additionInfo);
    sendTrackingData('checkout_page_information_saved', accountId, undefined, additionInfo);
  };

  const sendFilledFieldTracking = (field: string, isValid: boolean, errorCode?: string) => {
    const convertedField = fieldTrackingInfoMapper[field];
    sendTrackingData('checkout_page_all_action', accountId, undefined, withCartSnapshot({
      field: 'payment',
      object: convertedField.name,
      action: 'fill',
      is_required: convertedField.isRequired,
      is_valid: isValid,
      error: isValid ? undefined : errorCode,
      ...trackingCookies,
    }, cartSnapshot));
  };

  const onFieldFilled = (event: StripeElementChangeEvent) => {
    // Field is blurred and has error
    if (event.error) {
      sendFilledFieldTracking(event.elementType, false, event.error.code);
      return;
    }
    // Field is marked as complete
    if (event.complete) {
      sendFilledFieldTracking(event.elementType, true);
    }
  };
  const handleChangeCardNumber = (e: StripeCardNumberElementChangeEvent) => {
    setCurrentCardBrand(e.brand ?? 'unknown');
    setIsCardNumberValid(e.error === undefined && !e.empty && e.complete);
    setErrorMessage(e.error?.message);
    onFieldFilled(e);
  };
  const handleCVCChange = (e: StripeElementChangeEvent) => {
    setIsCardCvcValid(e.error === undefined && !e.empty && e.complete);
    setErrorMessage(e.error?.message);
    onFieldFilled(e);
  };
  const handleExpirationChange = (e: StripeElementChangeEvent) => {
    setIsExpiryDateValid(e.error === undefined && !e.empty && e.complete);
    setErrorMessage(e.error?.message);
    onFieldFilled(e);
  };

  const renderPaymentLogoList = (cardBrand: CardBrand) => (
    <img
      key={cardBrand.brand}
      src={cardBrand.src}
      alt="payment-logo"
      className={clsx(currentCardBrand.toLowerCase() === cardBrand.brand
        ? classes.paymentLogo
        : [classes.paymentLogo, classes.disablePaymentLogo])}
    />
  );

  const openCardForm = () => {
    dispatch(fromPaymentMethod.doEnableCardEditing(true));
    dispatch(fromPaymentMethod.doUpdateCardInformationInvalid(true));
  };

  return (
    <Box
      key="card-information"
      id="card-information"
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      pt={2}
    >
      <Box
        key="card-information-header"
        id="card-information-header"
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <Box
          key="card-information-title"
          id="card-information-title"
          display="flex"
          justifyContent="flex-start"
        >
          <Typography variant="h3" className={classes.checkoutTitle}>
            <FormattedMessage id="billinginfo.payment" />
          </Typography>
        </Box>
        <Box
          key="card-information--action-btn"
          id="card-information--action-btn"
          display="flex"
          flexDirection="row"
          justifyContent="flex-end"
        >
          {!isCardFormOpen && (
            <>
              {enableDelete && (
                <Box
                  key="card-information--cancel-btn"
                  id="card-information--cancel-btn"
                  display="flex"
                  flexDirection="row"
                  justifyContent="flex-end"
                  paddingRight={1}
                >
                  <PaymentMethodActionMenu
                    account={account}
                    currentCard={currentCard}
                    loading={loading}
                    handleCancel={handleCancel}
                    setLoading={setLoading}
                  />
                </Box>
              )}
              <Box
                key="card-information--update-btn"
                id="card-information--update-btn"
                display="flex"
                flexDirection="row"
                justifyContent="flex-end"
              >
                <Button
                  id="card-update-btn"
                  variant="outlined"
                  onClick={openCardForm}
                  disabled={loading || isReadOnly}
                  className={classes.button}
                >
                  <FormattedMessage id="billinginfo.button.update" />
                </Button>
              </Box>
            </>
          )}
          {isCardFormOpen && (
            <Grid>
              {enableSave && (
                <Button
                  id="card-submit-btn"
                  variant="outlined"
                  className={classes.saveBtn}
                  onClick={submitPaymentMethod}
                  disabled={loading || isReadOnly}
                >
                  {intl.formatMessage({ id: 'billinginfo.button.save' })}
                </Button>
              )}
              {!!currentCard && (
                <Button
                  id="card-cancel-btn"
                  variant="outlined"
                  color="inherit"
                  className={classes.cancelBtn}
                  onClick={handleCancel}
                  disabled={loading || isReadOnly}
                >
                  {intl.formatMessage({ id: 'billinginfo.button.cancel' })}
                </Button>
              )}
            </Grid>
          )}
        </Box>
      </Box>
      {!!currentCard && (
        <CurrentPaymentCard currentCard={currentCard} />
      )}
      {isCardFormOpen && (
        <>
          <Grid>
            <div>{CARD_BRAND_LIST.map(it => renderPaymentLogoList(it))}</div>
            <Grid container alignItems="flex-end" height={theme => theme.spacing(10)} paddingY={0}>
              <Grid item xs={6}>
                <Typography className={classes.cardInfoLabel} variant="subtitle2">Card Number</Typography>
                <CardInputWrapper>
                  <CardNumberElement options={NUMBER_OPTIONS} onChange={handleChangeCardNumber} />
                </CardInputWrapper>
              </Grid>
              <Grid item xs={4}>
                <Typography className={classes.cardInfoLabel} variant="subtitle2">
                  <FormattedMessage id="billinginfo.cardinfo.expiration" />
                </Typography>
                <CardInputWrapper>
                  <CardExpiryElement options={CARD_EXP_OPTIONS} onChange={handleExpirationChange} />
                </CardInputWrapper>
              </Grid>
              <Grid item xs={2}>
                <Typography className={classes.cardInfoLabel} variant="subtitle2">
                  <FormattedMessage id="billinginfo.cardinfo.cvc" />
                </Typography>
                <CardInputEndWrapper>
                  <CardCvcElement options={CARD_CVC_OPTIONS} onChange={handleCVCChange} />
                </CardInputEndWrapper>
              </Grid>
            </Grid>
            <Grid height={theme => theme.spacing(10)} paddingY={0}>
              <Typography className={classes.cardInfoLabel} variant="subtitle2">
                <FormattedMessage id="billinginfo.cardinfo.card_holder" />
              </Typography>
              <CardNameWrapper>
                <InputBase
                  className={classes.cardName}
                  placeholder="Name on card"
                  onChange={handleChangeName}
                  onBlur={handleCardHolderBlurred}
                />
              </CardNameWrapper>
            </Grid>
            {errorMessage && (
              <Typography variant="subtitle2" color="secondary" fontWeight={500} mb={2}>
                {errorMessage}
              </Typography>
            )}
            {loading && <LoadingProgress fullscreen />}
          </Grid>
          <Divider />
        </>
      )}
    </Box>
  );
});

export const BuiltInPaymentForm = forwardRef((props: CardInformationProps, ref) => (
  <Elements stripe={stripePromise}>
    <BuiltInPaymentFormInner ref={ref} {...props} />
  </Elements>
));
